import { z } from "zod";
import { zId } from "./common.js";

/**
 * Momentum-class AI outreach: signals detected from scores and touch history
 * become suggestions with grounded drafts attached, reviewed by a human in a
 * queue before anything sends. Nothing here auto-sends — ever.
 */

export const OUTREACH_SIGNALS = [
  "lapse_risk", // high lapse score and still reachable — win them back before they're gone
  "upgrade_ready", // high upgrade score — invite a bigger commitment
  "stale_touch", // portfolio donor with no human touch in 90+ days
  "first_gift", // brand-new donor — welcome them while it's warm
  "manual", // staff-initiated (batch drafting from a portfolio stage or segment)
] as const;
export const zOutreachSignal = z.enum(OUTREACH_SIGNALS);
export type OutreachSignal = z.infer<typeof zOutreachSignal>;

export const OUTREACH_STATUSES = ["suggested", "drafted", "sent", "dismissed"] as const;
export const zOutreachStatus = z.enum(OUTREACH_STATUSES);
export type OutreachStatus = z.infer<typeof zOutreachStatus>;

export const OUTREACH_CHANNELS = ["email", "sms"] as const;
export const zOutreachChannel = z.enum(OUTREACH_CHANNELS);
export type OutreachChannel = z.infer<typeof zOutreachChannel>;

/** batch drafting: a hand-picked set of constituents, one goal, one channel */
export const zOutreachBatchDraft = z.object({
  constituentIds: z.array(zId).min(1).max(25),
  channel: zOutreachChannel,
  goal: z.string().trim().min(1).max(300),
});
export type OutreachBatchDraft = z.infer<typeof zOutreachBatchDraft>;

export const zOutreachDismiss = z.object({
  reason: z.string().trim().max(300).optional(),
});

// ---------------------------------------------------------------------------
// CT-RM-16: next-best-action ranking of the outreach queue
// ---------------------------------------------------------------------------

/**
 * The queue is ranked by a deterministic, documented formula — no model, no
 * learned weights. Each factor maps raw record data to a 0–100 sub-score;
 * the priority is the weight-averaged sub-score:
 *
 *   priority = Σ (weight_i × score_i) / Σ weight_i          (0–100)
 *
 * A factor's "points" in the breakdown is weight_i × score_i / Σ weight_i,
 * so the points of every factor add up to the priority exactly.
 * Missing data (no score yet, no portfolio, no open asks) scores 0 — it never
 * pushes someone up the queue. Ties break by newest suggestion first.
 */
export const OUTREACH_RANK_FACTORS = [
  "engagement",
  "lapseRisk",
  "stage",
  "openAsks",
  "closeDate",
  "touchGap",
  "movesDue",
  "giftRecency",
] as const;
export type OutreachRankFactor = (typeof OUTREACH_RANK_FACTORS)[number];

/** what each factor measures and how it is scored — shown verbatim in settings and the breakdown */
export const OUTREACH_FACTOR_DOCS: Record<OutreachRankFactor, { label: string; rule: string }> = {
  engagement: { label: "Engagement score", rule: "The scoring module's engagement level (0–100), used as-is." },
  lapseRisk: { label: "Lapse risk", rule: "The scoring module's lapse risk (0–100), used as-is." },
  stage: {
    label: "Portfolio stage",
    rule: "Solicitation 100 · cultivation 70 · stewardship 50 · qualification 40 · identification 20 · not in a portfolio 0.",
  },
  openAsks: {
    label: "Open ask amount",
    rule: "Sum of open asks (planned, asked, verbal, committed) on a log scale: $100 → 40, $1,000 → 60, $10,000 → 80, $100,000+ → 100.",
  },
  closeDate: {
    label: "Expected close",
    rule: "Nearest expected close on an open ask: overdue or today → 100, falling linearly to 0 at 90 days out. No date → 0.",
  },
  touchGap: {
    label: "Days since last touch",
    rule: "Days since the last human touch (note, mail, outbound email/SMS, event, gift, completed task), linear to 100 at 180 days. Never touched → 100.",
  },
  movesDue: {
    label: "Planned moves due",
    rule: "The officer's next open planned move for this donor: overdue or due today → 100, within 7 days → 70, within 30 days → 40, later or none → 0.",
  },
  giftRecency: {
    label: "Last gift recency",
    rule: "How recently they gave: a gift today → 100, falling linearly to 0 at 730 days. Never gave → 0.",
  },
};

export const zOutreachRankWeights = z.object(
  Object.fromEntries(OUTREACH_RANK_FACTORS.map((f) => [f, z.number().int().min(0).max(100)])) as Record<
    OutreachRankFactor,
    z.ZodNumber
  >,
);
export type OutreachRankWeights = Record<OutreachRankFactor, number>;

/** sane defaults (sum to 100 so each weight reads as a percentage) */
export const DEFAULT_OUTREACH_WEIGHTS: OutreachRankWeights = {
  engagement: 15,
  lapseRisk: 20,
  stage: 10,
  openAsks: 15,
  closeDate: 15,
  touchGap: 10,
  movesDue: 10,
  giftRecency: 5,
};

/** raw record data the formula reads — every field nullable when the data does not exist */
export interface OutreachRankInputs {
  engagementLevel: number | null;
  lapseRisk: number | null;
  stage: string | null;
  /** cents */
  openAskAmount: number;
  /** days until the nearest expected close on an open ask (negative = overdue) */
  daysToClose: number | null;
  /** null = never touched */
  daysSinceTouch: number | null;
  /** days until the next open planned move is due (negative = overdue) */
  daysToMoveDue: number | null;
  /** null = never gave */
  daysSinceGift: number | null;
}

export interface OutreachRankFactorResult {
  factor: OutreachRankFactor;
  label: string;
  /** human-readable raw input, e.g. "$12,000 open" */
  detail: string;
  /** 0–100 sub-score */
  score: number;
  weight: number;
  /** contribution to the priority; contributions sum to the priority */
  points: number;
}

export interface OutreachRank {
  priority: number;
  factors: OutreachRankFactorResult[];
}

const STAGE_SCORES: Record<string, number> = {
  solicitation: 100,
  cultivation: 70,
  stewardship: 50,
  qualification: 40,
  identification: 20,
};

const clamp = (n: number) => Math.max(0, Math.min(100, n));
const round1 = (n: number) => Math.round(n * 10) / 10;
const usd = (cents: number) => `$${Math.round(cents / 100).toLocaleString("en-US")}`;
const days = (n: number) => `${n} day${n === 1 ? "" : "s"}`;

/** each factor's 0–100 sub-score and a readable description of the raw input */
export function outreachFactorScores(i: OutreachRankInputs): Record<OutreachRankFactor, { score: number; detail: string }> {
  const dollars = i.openAskAmount / 100;
  return {
    engagement: {
      score: clamp(i.engagementLevel ?? 0),
      detail: i.engagementLevel == null ? "not scored yet" : `${i.engagementLevel}/100`,
    },
    lapseRisk: {
      score: clamp(i.lapseRisk ?? 0),
      detail: i.lapseRisk == null ? "not scored yet" : `${i.lapseRisk}/100`,
    },
    stage: {
      score: i.stage ? (STAGE_SCORES[i.stage] ?? 0) : 0,
      detail: i.stage ?? "not in a portfolio",
    },
    openAsks: {
      score: dollars >= 1 ? clamp(Math.round(20 * Math.log10(dollars))) : 0,
      detail: i.openAskAmount > 0 ? `${usd(i.openAskAmount)} open` : "no open asks",
    },
    closeDate: {
      score: i.daysToClose == null ? 0 : i.daysToClose <= 0 ? 100 : clamp(Math.round(100 * (1 - i.daysToClose / 90))),
      detail:
        i.daysToClose == null
          ? "no expected close"
          : i.daysToClose < 0
            ? `${days(-i.daysToClose)} overdue`
            : i.daysToClose === 0
              ? "closes today"
              : `closes in ${days(i.daysToClose)}`,
    },
    touchGap: {
      score: i.daysSinceTouch == null ? 100 : clamp(Math.round((i.daysSinceTouch / 180) * 100)),
      detail: i.daysSinceTouch == null ? "never touched" : `${days(i.daysSinceTouch)} since last touch`,
    },
    movesDue: {
      score: i.daysToMoveDue == null ? 0 : i.daysToMoveDue <= 0 ? 100 : i.daysToMoveDue <= 7 ? 70 : i.daysToMoveDue <= 30 ? 40 : 0,
      detail:
        i.daysToMoveDue == null
          ? "no planned move"
          : i.daysToMoveDue < 0
            ? `move ${days(-i.daysToMoveDue)} overdue`
            : i.daysToMoveDue === 0
              ? "move due today"
              : `move due in ${days(i.daysToMoveDue)}`,
    },
    giftRecency: {
      score: i.daysSinceGift == null ? 0 : clamp(Math.round(100 * (1 - i.daysSinceGift / 730))),
      detail: i.daysSinceGift == null ? "never gave" : `last gift ${days(i.daysSinceGift)} ago`,
    },
  };
}

/** the ranking formula: weighted average of the factor sub-scores, with a per-factor breakdown */
export function rankOutreach(inputs: OutreachRankInputs, weights: OutreachRankWeights): OutreachRank {
  const scores = outreachFactorScores(inputs);
  const total = OUTREACH_RANK_FACTORS.reduce((s, f) => s + Math.max(0, weights[f] ?? 0), 0);
  const factors = OUTREACH_RANK_FACTORS.map((f) => {
    const weight = Math.max(0, weights[f] ?? 0);
    return {
      factor: f,
      label: OUTREACH_FACTOR_DOCS[f].label,
      detail: scores[f].detail,
      score: scores[f].score,
      weight,
      points: total > 0 ? round1((weight * scores[f].score) / total) : 0,
    };
  });
  const priority = total > 0 ? round1(OUTREACH_RANK_FACTORS.reduce((s, f) => s + Math.max(0, weights[f] ?? 0) * scores[f].score, 0) / total) : 0;
  return { priority, factors };
}

export const OUTREACH_SORTS = ["priority", "officer", "newest"] as const;
export type OutreachSort = (typeof OUTREACH_SORTS)[number];

// ---------------------------------------------------------------------------
// CT-RM-16: per-org monthly cap on paid AI draft generation
// ---------------------------------------------------------------------------

/** default monthly cap on AI drafts per org; admins can raise or lower it */
export const DEFAULT_MONTHLY_DRAFT_CAP = 500;
/** default estimated cost per draft, in cents (shown in settings — an estimate, not a bill) */
export const DEFAULT_DRAFT_COST_CENTS = 2;

export const zOutreachSettingsUpdate = z.object({
  weights: zOutreachRankWeights
    .refine((w) => Object.values(w).some((v) => v > 0), { message: "At least one weight must be above zero" })
    .optional(),
  /** null = no cap */
  monthlyDraftCap: z.number().int().min(0).max(100_000).nullable().optional(),
  estDraftCostCents: z.number().int().min(0).max(10_000).optional(),
});
export type OutreachSettingsUpdate = z.infer<typeof zOutreachSettingsUpdate>;

export interface OutreachSettings {
  weights: OutreachRankWeights;
  defaults: OutreachRankWeights;
  monthlyDraftCap: number | null;
  estDraftCostCents: number;
  usage: {
    /** YYYY-MM (UTC) */
    period: string;
    drafts: number;
    /** cents */
    estCostCents: number;
    remaining: number | null;
    capReached: boolean;
  };
}

/** editable draft saved back onto a suggestion before sending */
export const zOutreachUpdateDraft = z.object({
  subject: z.string().trim().max(300).optional(),
  body: z.string().trim().min(1).max(10_000),
});
export type OutreachUpdateDraft = z.infer<typeof zOutreachUpdateDraft>;
